import React from 'react';
import PropTypes from 'prop-types';
import { LinearProgress, Typography, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

export default function LinearProgressLabel({ quarter, progressContainerWidth = '75%' }) {
  const { t, i18n } = useTranslation();

  const endOfQuarter = quarter?.endDate ? new Date(quarter.endDate) : null;
  const startOfQuarter = quarter?.startDate ? new Date(quarter.startDate) : null;

  const daysLeft = endOfQuarter ? Math.ceil((endOfQuarter.getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : null;
  const daysCompleted = startOfQuarter ? Math.floor((Date.now() - startOfQuarter.getTime()) / (1000 * 60 * 60 * 24)) : null;

  const daysInQuarter = endOfQuarter
    ? Math.ceil((endOfQuarter.getTime() - startOfQuarter.getTime()) / (1000 * 60 * 60 * 24))
    : null;

  const progressValue = (() => {
    if (daysCompleted === null || daysInQuarter === null || daysInQuarter <= 1) {
      return 0;
    }

    const rawValue = ((daysCompleted - 1) * 100) / (daysInQuarter - 1);
    return Math.max(0, Math.min(100, rawValue));
  })();

  const formatDate = date => {
    return date ? date.toLocaleDateString(i18n.language, { year: 'numeric', month: 'short', day: 'numeric' }) : '';
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
      <Typography variant="h6" sx={{ mb: 0.5, textAlign: 'center' }}>
        {quarter.internalName}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', width: progressContainerWidth }}>
        <Typography variant="caption" sx={{ mr: 1, minWidth: 80, textAlign: 'right' }}>
          {formatDate(startOfQuarter)}
        </Typography>
        <Box sx={{ flex: 2 }}>
          <LinearProgress sx={{ height: 20, borderRadius: 5 }} variant="determinate" value={progressValue} />
        </Box>
        <Typography variant="caption" sx={{ ml: 1, minWidth: 80 }}>
          {formatDate(endOfQuarter)}
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
        {t('home.daysLeft', { days: daysLeft })}
      </Typography>
    </Box>
  );
}

LinearProgressLabel.propTypes = {
  quarter: PropTypes.shape({
    internalName: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
  }).isRequired,
  progressContainerWidth: PropTypes.string,
};
